<template>
  <div class="my-orders">
    <div class="container d-flex justify-content-between">
      <h2 class="my-orders__heading">
        Order history
      </h2>

      <download-csv
        label="Download"
        file-name="Order history"
        :format-keys="csv.orders.formatKeys"
        :blacklist-keys="csv.orders.blacklistKeys"
        :input-data="csv.orders.data">
      </download-csv>
    </div>

    <div class="my-orders__container">
      <div v-if="loading"
           class="my-orders__loading-container">
        <div class="my-orders__loading-content">
          <h6>Loading order history</h6>
          <font-awesome-icon icon="spinner"
                             class="icon"
                             size="lg"
                             spin>
          </font-awesome-icon>
        </div>
      </div>

      <!-- Commented out for now, will be put back in -->

      <div class="my-orders__filter">
        <span class="my-orders__filter-header">Filters:</span>
        <!-- <b-checkbox-group v-model="filterOptions">
      <b-checkbox value="all-products">
        All products
      </b-checkbox>
      <b-checkbox value="consultancy">
        Consultancy
      </b-checkbox>
      <b-checkbox value="resources">
        Resources
      </b-checkbox>
      <b-checkbox value="subscription">
        Subscription
      </b-checkbox>
      <b-checkbox value="training">
        Training
      </b-checkbox>
      </b-checkbox-group>-->

        <div class="my-orders__filter-item">
          <b-form-select
            v-model="selectedTime"
            class="my-orders__filter-time"
            :options="timeOptions"></b-form-select>
          <b-form-select
            v-model="selectedOrganisation"
            class="my-orders__filter-org"
            :options="orgOptions"></b-form-select>
        </div>

        <div class="my-orders__filter_cta">
          <button
            class="btn btn-sm refresh-btn"
            :disabled="loading"
            aria-label="Refresh orders"
            @click="fetchData">
            <font-awesome-icon icon="sync-alt"
                               class="align-right icon"></font-awesome-icon>
            Refresh
          </button>
        </div>
      </div>

      <template v-if="filteredOrders && filteredOrders.length > 0">
        <orders-table
          class="my-orders__table"
          :orders="filteredOrders"
          @refreshPage="fetchData"></orders-table>

        <div class="my-orders__cards">
          <order-item
            v-for="(item, index) in filteredOrders"
            :key="index"
            :order="item"></order-item>
        </div>
      </template>
      <template v-else>
        <div class="my-orders__empty">
          <p>No orders found.</p>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { subDays, subMonths, subYears } from "date-fns";
import OrdersTable from "~/Components/OrdersHistory/OrdersTable.vue";
import OrderItem from "~/Components/OrdersHistory/OrderItem.vue";
import { loadOrderHistory } from "~/Scripts/resources/ordersResource";
import DownloadCsv from "~/Components/Downloads/DownloadCsv.vue";
import { orderBy } from "lodash";
// import { cancelRefundOrderLine } from "../../Scripts/resources/ordersResource";

export default {
  name: "MyOrders",
  components: {
    OrdersTable,
    OrderItem,
    DownloadCsv
  },
  props: {
    orgId: {
      type: String,
      default: null
    },
    webAccountId: {
      type: String,
      default: null
    },
    loadByOrgId: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      orders: [],
      selectedTime: null,
      selectedOrganisation: null,
      loading: false,
      filterOptions: ["all-products", "consultancy", "resources", "subscription", "training"],
      timeOptions: [
        {
          value: null,
          text: "Please select a time frame"
        },
        {
          value: "month",
          text: "Last 30 days"
        },
        {
          value: "six-months",
          text: "Last 6 months"
        },
        {
          value: "year",
          text: "Last year"
        },
        {
          value: "all",
          text: "All orders"
        }
      ]
    };
  },
  computed: {
    filteredOrders () {
      let result = null;

      switch ( this.selectedTime ) {
        case "month": {
          result = this.orders.filter(
            o => new Date( o.dateOrdered ) >= new Date( subDays( Date.now(), 30 ) )
          );
          break;
        }
        case "six-months": {
          result = this.orders.filter(
            o => new Date( o.dateOrdered ) >= new Date( subMonths( Date.now(), 6 ) )
          );
          break;
        }
        case "year": {
          result = this.orders.filter(
            o => new Date( o.dateOrdered ) >= new Date( subYears( Date.now(), 1 ) )
          );
          break;
        }
        case "all":
        default:
          result = this.orders;
          break;
      }

      let mutableResult = [...result];

      mutableResult.sort( ( a, b ) => new Date( b.dateOrdered ) - new Date( a.dateOrdered ) );

      if ( this.selectedOrganisation ) {
        mutableResult = result.filter( product => product.orgName === this.selectedOrganisation );
      }

      // console.log( mutableResult );
      // console.log( JSON.stringify( mutableResult ) );

      return mutableResult;
    },
    orgOptions () {
      const opts = [
        {
          value: null,
          text: "Please select an organisation"
        }
      ];

      const orgs = this.orders.map( order => ( { value: order.orgName, text: order.orgName } ) );
      const sortedOrgs = orderBy( orgs, ["value"], ["asc"] );
      const combined = [...opts, ...sortedOrgs];

      return this.removeDuplicatesFromArray( combined, "value" );
    },
    csv () {
      const csv = { orders: { blacklistKeys: [], data: [], formatKeys: [] } };

      if ( this.filteredOrders == null || this.filteredOrders.length < 0 ) {
        return csv;
      }

      const rows = this.filteredOrders
        .map( order => {
          const { itemsInOrder } = order;

          return itemsInOrder.map( item => {
            const csvItem = {};
            delete Object.assign( csvItem, item, { pricePerItem: item.lineValue } ).lineValue;
            return { ...order, ...csvItem };
          } );
        } )
        .reduce( ( acc, curr ) => acc.concat( curr ), [] );

      csv.orders.data = rows;
      csv.orders.blacklistKeys = [
        "orderHeaderId",
        "showRefundOption",
        "orderLineId",
        "creditOrderId",
        "itemsInOrder",
        "productId",
        "creditOrderLineId",
        "statusText",
        "statusId"
      ];

      csv.orders.formatKeys = [
        { key: "dateOrdered", type: "date" },
        { key: "totalCost", type: "currency", name: "Total cost exc. vat" },
        { key: "pricePerItem", type: "currency" }
      ];

      // console.log( csv );

      return csv;
    }
  },
  mounted () {
    this.fetchData();
  },
  methods: {
    fetchData () {
      this.loading = true;
      let orgIdParam = null;
      let webAccountIdParam = null;

      if ( this.loadByOrgId ) {
        orgIdParam = this.orgId;
      } else {
        webAccountIdParam = this.webAccountId;
      }

      loadOrderHistory( orgIdParam, webAccountIdParam )
        .then( result => {
          if ( result && result.data ) {
            this.orders = result.data;
          }
        } )
        .catch( error => {
          console.log( "error occured when loading order history", error );
          throw error;
        } )
        .finally( () => {
          this.loading = false;
        } );
    },
    removeDuplicatesFromArray ( array, key ) {
      const lookup = {};
      const result = [];

      array.forEach( element => {
        if ( !lookup[element[key]] ) {
          lookup[element[key]] = true;
          result.push( element );
        }
      } );

      return result;
    },
    isDate ( dateStr ) {
      return !Number.isNaN( new Date( dateStr ).getDate() );
    }
  }
};
</script>

<style lang="less" scoped>
.my-orders {
  position: relative;

  &__container {
    max-height: 100%;
    overflow-x: auto;
    position: relative;
  }

  &__heading {
    letter-spacing: normal;
    font-weight: bold;
    font-size: 16pt;
    margin-top: 0;
    margin-bottom: 30px;
  }

  &__filter {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 2px solid #b2d6d9;
    background-color: #fff;
    padding: 10px 10px 6px;
    margin-bottom: 20px;

    @media screen and (max-width: 768px) {
      flex-flow: column;
    }

    &-header {
      font-weight: 600;
      color: #0c499c;
      margin-right: 15px;
    }

    &-time {
      max-width: 240px;
      margin-bottom: 4px;
    }

    &-org {
      max-width: 240px;
      margin-bottom: 4px;
    }

    &-item {
      flex-grow: 1;

      @media screen and (max-width: 768px) {
        margin: 8px 0;
        text-align: center;
      }
    }

    &_cta {
      display: flex;

      &-download {
        background-color: #007a7f;
        color: #fff;
        padding: 8px 16px;
        font-weight: 700;
        cursor: pointer;

        &:hover {
          background-color: #00383a;
        }
      }
    }
  }

  &__loading-container {
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    position: absolute;
    z-index: 100;
    pointer-events: none;
    background-color: rgba(255, 255, 255, 0.7);
  }

  &__loading-content {
    display: flex;
    align-items: center;
    h6 {
      margin-bottom: 0;
    }

    .icon {
      margin: 10px;
    }
  }

  &__table {
    @media screen and (max-width: 991px) {
      display: none;
    }
  }

  &__cards {
    @media screen and (min-width: 992px) {
      display: none;
    }
  }

  &__empty {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
  }
}
</style>
